import { useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { IAutoAckRule, IAutoAckRuleInput } from "@home-health-notify/domain";
import {
  ApiErrorHandler,
  UserDirectoryContext,
  useAutoAck,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import AutoAckRuleDialog from "./AutoAckRuleDialog";
import {
  CardLabel,
  EmptyRing,
  EmptyState,
  PageHead,
  RuleCard,
} from "./AutoAck.style";
import {
  describePatientCriteria,
  matchedUsers,
  selectableUsers,
  userChipProps,
  userLabelWithTypeAndStatus,
} from "./autoAckUtils";

// A long chip list stops being readable; the rest are reported as a count.
const CHIP_DISPLAY_LIMIT = 12;

export const AutoAckContent = () => {
  const { t } = useTranslation();
  const tNs = (key: string, options?: object) =>
    t(key, { ns: "autoAck", ...options });
  const { enqueueSnackbar } = useSnackbar();
  const { userDirectory } = useContext(UserDirectoryContext);
  const {
    events,
    rules,
    isLoading,
    isSaving,
    hasLoadError,
    reload,
    saveRule,
    deleteRule,
  } = useAutoAck();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<IAutoAckRule | undefined>();
  const [rulePendingDelete, setRulePendingDelete] = useState<IAutoAckRule>();

  const users = useMemo(
    () => selectableUsers(userDirectory.getUserDirectory()),
    [userDirectory],
  );
  const eventsByKey = useMemo(
    () => new Map(events.map(event => [event.key, event])),
    [events],
  );
  const openAddDialog = () => {
    setEditingRule(undefined);
    setIsDialogOpen(true);
  };

  const openEditDialog = (rule: IAutoAckRule) => {
    setEditingRule(rule);
    setIsDialogOpen(true);
  };

  const handleSave = async (rule: IAutoAckRuleInput, ruleId?: string) => {
    try {
      await saveRule(rule, ruleId);
      setIsDialogOpen(false);
      enqueueSnackbar(tNs("saved"), { variant: "success" });
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    }
  };

  const handleDelete = async () => {
    if (!rulePendingDelete) return;
    try {
      await deleteRule(rulePendingDelete.id);
      enqueueSnackbar(tNs("deleted_success"), { variant: "success" });
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    } finally {
      setRulePendingDelete(undefined);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHead>
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {tNs("title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tNs("subtitle")}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
          disabled={isLoading || hasLoadError}
        >
          {tNs("addRule")}
        </Button>
      </PageHead>

      {hasLoadError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              {tNs("retry")}
            </Button>
          }
        >
          {tNs("loadError")}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        !hasLoadError &&
        (rules.length === 0 ? (
          <EmptyState>
            <EmptyRing>
              <TaskAltIcon fontSize="large" />
            </EmptyRing>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              {tNs("emptyTitle")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 420 }}
            >
              {tNs("emptyBody")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddDialog}
            >
              {tNs("addRule")}
            </Button>
          </EmptyState>
        ) : (
          rules.map(rule => {
            const ruleUsers = matchedUsers(rule.userCriteria, users);
            return (
              <RuleCard key={rule.id} variant="outlined">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CardLabel>{tNs("events")}</CardLabel>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}
                  >
                    {rule.events.map(eventKey => {
                      const event = eventsByKey.get(eventKey);
                      const isLab = eventKey.startsWith("patient:lab:");
                      return (
                        <Chip
                          key={eventKey}
                          size="small"
                          label={event?.displayName ?? eventKey}
                          color={isLab ? "warning" : "primary"}
                          variant={isLab ? "outlined" : "filled"}
                        />
                      );
                    })}
                  </Box>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {tNs("configuredCount", { count: ruleUsers.length })}
                    {describePatientCriteria(rule.patientCriteria, tNs)}.{" "}
                    {tNs("onlyGroupMembersAsked")}
                  </Typography>
                  {ruleUsers.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                        mt: 0.75,
                      }}
                    >
                      {ruleUsers.slice(0, CHIP_DISPLAY_LIMIT).map(user => (
                        <Chip
                          key={user.id}
                          size="small"
                          label={userLabelWithTypeAndStatus(user, tNs)}
                          {...userChipProps(user)}
                        />
                      ))}
                      {ruleUsers.length > CHIP_DISPLAY_LIMIT && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`+${ruleUsers.length - CHIP_DISPLAY_LIMIT}`}
                        />
                      )}
                    </Box>
                  )}
                </Box>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => openEditDialog(rule)}
                    disabled={!rule.isEditable}
                    title={
                      rule.isEditable ? undefined : tNs("readOnlyRuleHint")
                    }
                    data-testid={`auto-ack-edit-${rule.id}`}
                    aria-label={tNs("editRule")}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setRulePendingDelete(rule)}
                    aria-label={tNs("delete")}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </RuleCard>
            );
          })
        ))
      )}

      {isDialogOpen && (
        <AutoAckRuleDialog
          open
          events={events}
          rule={editingRule}
          isSaving={isSaving}
          onClose={() => setIsDialogOpen(false)}
          onSave={handleSave}
        />
      )}

      <Dialog
        open={!!rulePendingDelete}
        onClose={() => setRulePendingDelete(undefined)}
      >
        <DialogTitle>{tNs("deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {tNs("deleteBody", {
              events:
                rulePendingDelete?.events
                  .map(key => eventsByKey.get(key)?.displayName ?? key)
                  .join(", ") ?? "",
            })}
          </Typography>
          <Typography variant="body2">{tNs("deleteIrreversible")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRulePendingDelete(undefined)}>
            {tNs("cancel")}
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            {tNs("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
